import StatusChip from 'components/@extended/StatusChip';
import { OFF_SECONDARY_CLASS } from 'components/buttons/buttonStyle';
import ConcurrentEditingBanner from 'components/forms/ConcurrentEditingBanner';
import { MOD_ENTER_HINT } from 'components/global-search/GlobalSearchBar';
import { FIELD_GRID_CLASS, GridCells, Row, spansRow } from 'components/GridItemList';
import { CircleAlert as CircleAlertIcon, Loader2Icon, PencilIcon } from 'components/icons';
import { Button, buttonVariants } from 'components/ui/button';
import {
    createContext,
    KeyboardEvent,
    ReactNode,
    Ref,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Control, FieldValues, FormProvider, get, UseFormReturn, useFormState, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { ERROR_CODE_MESSAGES } from 'store/api/portalAPI';
import { cn } from 'utils/cn';
import { FormKey } from 'utils/formAnalytics';
import { t } from 'utils/getTranslations';
import { rejectedSubmitHandler } from 'utils/useRejectedSubmitCapture';
import { InSectionEditFormContext } from './sectionEditFields';
import { SectionSaveRefused } from './SectionSaveRefused';
import SectionUnsavedChangesGuard from './SectionUnsavedChangesGuard';
import { SectionEditingResource, useSectionEditingSession } from './useSectionEditingSession';

interface SectionEditState {
    canEdit: boolean;
    /** The section in edit, or the one `?edit=` named while its tab is not shown. */
    editingId: string | null;
    /**
     * The section in edit whose tab is shown, from the press on Edit: its editor may still be mounted hidden. While
     * there is one, the others keep only the box of their Edit.
     */
    shownId: string | null;
    open: (id: string) => void;
    show: (id: string) => void;
    close: (id: string) => void;
    session?: SectionEditingResource;
}

const SectionEditContext = createContext<SectionEditState | null>(null);

export { useInSectionEditForm } from './sectionEditFields';

const InRecordSectionContext = createContext(false);

/**
 * True only inside a {@link SectionEditForm}: a record section in edit, whose fields take the cells of the read
 * view they replace. {@link useInSectionEditForm} is true in a create dialog's sections too, which have no read
 * view: what is drawn to keep one in place goes by this hook, so a dialog draws none of it.
 */
export const useInRecordSection = () => useContext(InRecordSectionContext);

/**
 * The span, in a record section, of a field whose value may need the whole row: its read cell's, as
 * {@link spansRow} has it, by the value the form opened with. So Edit moves no cell, the field keeps its place while
 * it is typed in, and the row is laid out anew once its saved value is read again. Outside a section there is none.
 */
export function useSectionWideClass<T extends FieldValues>(control: Control<T>, name: string) {
    const inSection = useInRecordSection();
    const { defaultValues } = useFormState({ control });
    return inSection && spansRow(get(defaultValues, name)) ? 'col-span-full' : undefined;
}

// What an open section's editor calls once it has something to show. Outside one nothing waits for it.
const SectionEditorReadyContext = createContext<() => void>(() => undefined);

// The open section's rows no field edits, for its form to draw under its fields.
const SectionReadOnlyRowsContext = createContext<Row[]>([]);

// Where a section's form draws what only its editor shows: under everything its read view has.
const SectionFormEndContext = createContext<HTMLElement | null>(null);

/**
 * What only the editor draws and no read row shows: a field with no stored value, a hint, a notice. In a record
 * section it goes under the fields and the rows no field edits, in the document's order too, so that nothing the
 * read view shows moves on Edit. Anywhere else it stays where it is written.
 */
export function SectionFormEnd({ children }: { children: ReactNode }) {
    const inSection = useInRecordSection();
    const end = useContext(SectionFormEndContext);
    if (!inSection) {
        return <>{children}</>;
    }
    return end ? createPortal(children, end) : null;
}

function useSectionEditorReady() {
    const ready = useContext(SectionEditorReadyContext);
    useEffect(() => {
        ready();
    }, [ready]);
}

/**
 * Shows an open section's editor in place of its read view. {@link SectionEditForm} does it for a form; this is
 * for what an editor draws instead of one and the user must see, such as an error.
 */
export function SectionEditorReady() {
    useSectionEditorReady();
    return null;
}

interface ProviderProps {
    /** The record's Edit gate, as its edit button had it; without it no section edits. */
    canEdit: boolean;
    /** The editing session a section in edit holds, when the record has one. */
    session?: SectionEditingResource;
    children: ReactNode;
}

/**
 * A record page's edit state, above its tabs because sections sit on several: one section edits at a time.
 * `?edit=<section id>` opens that section; it is read on arrival, as a tab redirect may drop it before the
 * section mounts.
 */
export function SectionEditProvider({ canEdit, session, children }: ProviderProps) {
    const [searchParams] = useSearchParams();
    const requestedId = searchParams.get('edit');
    const [seenRequestId, setSeenRequestId] = useState(requestedId);
    const [editingId, setEditingId] = useState(requestedId);
    const [shownId, setShownId] = useState<string | null>(null);
    if (requestedId !== seenRequestId) {
        setSeenRequestId(requestedId);
        if (requestedId) {
            setEditingId(requestedId);
        }
    }

    const open = useCallback((id: string) => setEditingId(id), []);
    const show = useCallback((id: string) => setShownId(id), []);
    const close = useCallback((id: string) => {
        setEditingId((current) => (current === id ? null : current));
        setShownId((current) => (current === id ? null : current));
    }, []);

    const value = useMemo<SectionEditState>(
        () => ({ canEdit, editingId: canEdit ? editingId : null, shownId, open, show, close, session }),
        [canEdit, editingId, shownId, open, show, close, session]
    );
    return <SectionEditContext.Provider value={value}>{children}</SectionEditContext.Provider>;
}

interface SectionEditProps {
    /** The section's `?edit=` id. */
    id: string;
    titleId: string;
    /** The read view, handed the section's Edit control: `null` when it cannot edit, Edit's empty box while another does. */
    children: (edit: ReactNode) => ReactNode;
    /** The section's fields and their save, mounted from the press on Edit; `close` returns it to read mode. */
    editor: (close: () => void) => ReactNode;
    /** Classes of the section while it edits, such as the spacing its read view carries itself. */
    className?: string;
    /** This section's own gate, on top of the provider's: without it the section only reads, `?edit=` included. */
    canEdit?: boolean;
    /** The read view's rows: those no field edits (`readOnly`) stay under the fields, as text, while it edits. */
    rows?: Row[];
    /** The message of the section's Edit, for a section whose Edit names what it edits; "Edit" without one. */
    editLabelId?: string;
}

/**
 * A record section that edits in place: Edit in its header turns its read view into its form fields. The read view
 * stays, its Edit busy, until the editor has its form to show, and the section then switches in one step. Outside
 * a {@link SectionEditProvider} it is the read view alone. Focus follows: into the first field as the fields show,
 * and back onto Edit as the section closes, by Save, Cancel or Esc.
 */
export function SectionEdit({
    id,
    titleId,
    children,
    editor,
    className,
    canEdit = true,
    rows,
    editLabelId = 'edit',
}: SectionEditProps) {
    const state = useContext(SectionEditContext);
    const [isReady, setIsReady] = useState(false);
    const ready = useCallback(() => setIsReady(true), []);
    const isOpen = !!state && canEdit && state.editingId === id;
    if (!isOpen && isReady) {
        setIsReady(false);
    }
    // The fields, and Save and Cancel with them, are gone as the section closes: focus would be left on the page.
    // Edit is back in the read view by then, a new node each time. Focus the user has put on another control
    // meanwhile, as while the save ran, stays there.
    const editRef = useRef<HTMLButtonElement>(null);
    const wasOpen = useRef(false);
    useEffect(() => {
        if (wasOpen.current && !isOpen && isFocusUnclaimed()) editRef.current?.focus();
        wasOpen.current = isOpen;
    }, [isOpen]);
    if (!state) {
        return <>{children(null)}</>;
    }
    const edit = isOpen ? (
        <PendingEditButton labelId={editLabelId} />
    ) : canEdit && state.canEdit ? (
        <SectionEditButton ref={editRef} labelId={editLabelId} onClick={() => state.open(id)} />
    ) : null;
    // Both keep their place here whichever is shown: the read view is not remounted by Edit, nor the editor, with
    // its form state and its queries, by becoming ready.
    return (
        <>
            {isOpen && isReady ? null : children(edit)}
            {isOpen ? (
                <SectionReadOnlyRowsContext.Provider value={rows?.filter((row) => row.readOnly) ?? NO_ROWS}>
                    <EditingSection
                        id={id}
                        titleId={titleId}
                        editor={editor}
                        className={className}
                        editLabelId={editLabelId}
                        state={state}
                        isReady={isReady}
                        ready={ready}
                    />
                </SectionReadOnlyRowsContext.Provider>
            ) : null}
        </>
    );
}

const NO_ROWS: Row[] = [];

/** The id of the section that edits, whose tab is shown; null while none does or outside a provider. */
export const useEditingSectionId = () => useContext(SectionEditContext)?.shownId ?? null;

/** True while a section of the page edits: an Edit that opens a modal waits meanwhile, unseen, as the other cards' do. */
export const useIsSectionEditing = () => useEditingSectionId() != null;

/** Opens a section in edit from outside it, as another card's link to it does; nothing outside a provider. */
export const useOpenSection = () => useContext(SectionEditContext)?.open;

/**
 * A card's Edit. While a section of the page edits there is none, only its box, so a card that edits in a modal
 * instead waits for it too.
 */
export function SectionEditButton({
    onClick,
    ref,
    labelId = 'edit',
}: {
    onClick: () => void;
    ref?: Ref<HTMLButtonElement>;
    labelId?: string;
}) {
    return useIsSectionEditing() ? (
        <EditButtonBox labelId={labelId} />
    ) : (
        <Button ref={ref} variant="ghost" size="sm" className={EDIT_BUTTON_CLASS} onClick={onClick}>
            <PencilIcon data-icon="inline-start" className="size-3.5" />
            <FormattedMessage id={labelId} />
        </Button>
    );
}

/** Edit in a card's header; an action beside it, such as Add entry, takes the same look. */
export const EDIT_BUTTON_CLASS =
    'text-primary hover:text-primary text-meta h-[30px] gap-1.5 rounded-md px-2.5 has-data-[icon=inline-start]:pl-2';

/**
 * The room Edit takes in a card's header, with nothing to see or press in it. The title beside it keeps its width,
 * and so its lines, whether the card shows Edit, hides it while another section edits, or edits itself.
 */
function EditButtonBox({ labelId = 'edit' }: { labelId?: string }) {
    return (
        <span
            aria-hidden
            className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), EDIT_BUTTON_CLASS, 'invisible')}
        >
            <PencilIcon data-icon="inline-start" className="size-3.5" />
            <FormattedMessage id={labelId} />
        </span>
    );
}

/** Edit once pressed, over the read view that stays until the editor can draw: the same box, busy. */
function PendingEditButton({ labelId }: { labelId: string }) {
    return (
        <Button variant="ghost" size="sm" className={EDIT_BUTTON_CLASS} disabled aria-busy>
            <Loader2Icon
                data-icon="inline-start"
                className="size-3.5 motion-safe:animate-spin motion-reduce:animate-pulse"
            />
            <FormattedMessage id={labelId} />
        </Button>
    );
}

function EditingSection({
    id,
    titleId,
    editor,
    className,
    editLabelId,
    state: { show, close, session },
    isReady,
    ready,
}: Omit<SectionEditProps, 'children' | 'rows'> & { state: SectionEditState; isReady: boolean; ready: () => void }) {
    const otherEditors = useSectionEditingSession(session);
    const sectionRef = useRef<HTMLElement>(null);

    // Unmounting closes the section too: switching tab leaves the next tab's sections their Edit.
    useEffect(() => {
        show(id);
        return () => close(id);
    }, [id, show, close]);
    const closeSection = useCallback(() => close(id), [close, id]);

    // The pressed Edit went with the read view, leaving focus on the page, where the section's own Esc and ⌘↵
    // are not heard: it goes to the first field once the fields can take it, which is not while they are hidden.
    // Not when the user has gone on to another control while the editor loaded.
    useEffect(() => {
        const section = sectionRef.current;
        if (isReady && section && isFocusUnclaimed()) {
            (section.querySelector('[data-section-fields]') ?? section)
                .querySelector<HTMLElement>(FIRST_FIELD_SELECTOR)
                ?.focus();
        }
    }, [isReady]);

    // Mounted from the press on Edit, and hidden until the editor is ready, so its loading draws nothing.
    // ponytail: no timeout. An editor that never gets its form leaves Edit busy over the read view; give the
    // wait a limit and an error if a section's queries turn out to hang.
    return (
        <section
            ref={sectionRef}
            hidden={!isReady}
            className={cn(
                'border-primary bg-card shadow-editing @container flex flex-col gap-3.5 overflow-hidden rounded-[10px] border-2 px-[19px] pt-[15px]',
                className
            )}
        >
            <div className="flex min-h-[30px] items-center gap-2">
                <h3 className="text-section m-0 min-w-0 flex-auto">
                    <FormattedMessage id={titleId} />
                </h3>
                {/* The chip sits in the room Edit had, as the read header's gap has it, so the title keeps its lines. */}
                <span className="grid flex-none items-center justify-items-end *:[grid-area:1/1]">
                    <EditButtonBox labelId={editLabelId} />
                    <StatusChip
                        type="neutral"
                        className="font-semibold"
                        label={<FormattedMessage id="sections.editing" />}
                    />
                </span>
            </div>
            {session ? (
                // The banner keeps its own markup; the new design draws it as the amber "changed" strip.
                <div className="[&>div]:border-changed-border [&>div]:bg-changed [&>div]:text-meta empty:hidden [&>div]:rounded-lg [&>div]:border [&>div]:px-3 [&>div]:py-2">
                    <ConcurrentEditingBanner editors={otherEditors} messageId={session.messageId} />
                </div>
            ) : null}
            <SectionEditorReadyContext.Provider value={ready}>
                {editor(closeSection)}
            </SectionEditorReadyContext.Provider>
        </section>
    );
}

// A section's first control the Tab key would stop at. The kit's selects, switches, checkboxes and radios keep a
// hidden input beside their control, out of the Tab order: it is skipped, and the control itself, an element with
// the role, is matched.
const FIRST_FIELD_SELECTOR = [
    'input:not([type="hidden"], [tabindex="-1"], :disabled)',
    'textarea:not(:disabled)',
    '[role="combobox"]:not([aria-disabled="true"])',
    ':is([role="switch"], [role="checkbox"], [role="radio"]):not([tabindex="-1"], [aria-disabled="true"])',
    'button:not([tabindex="-1"], :disabled)',
].join(', ');

// True while focus is where no user put it: on the page itself, as after the control that held it was removed, or
// on what a script focused for want of a control, such as the page's title.
const isFocusUnclaimed = () => {
    const focused = document.activeElement;
    return !(focused instanceof HTMLElement) || focused.tabIndex < 0;
};

// The rows no field edits, under the form's fields. One with a rule of its own steps aside while the form's values
// say a field has its value.
function ReadOnlyRows({ rows }: { rows: Row[] }) {
    const values = useWatch();
    return <GridCells rows={rows.filter(({ readOnly }) => typeof readOnly !== 'function' || readOnly(values))} />;
}

// A mapped error code has product copy. Failing that, a request the server refused (4xx) keeps the server's
// message: it names what it refused, which may be a field another section holds, and trying again as is cannot
// succeed. Anything else is a failure to reach or to answer, which trying again can fix.
const saveErrorText = (error: unknown): string => {
    if (error instanceof SectionSaveRefused) {
        return error.message;
    }
    const { status, data } = (error ?? {}) as { status?: unknown; data?: { code?: unknown; message?: unknown } };
    const known = typeof data?.code === 'string' ? ERROR_CODE_MESSAGES.get(data.code) : undefined;
    if (known) {
        return t(`error-code-message.${known}`);
    }
    const isRefusal = typeof status === 'number' && status >= 400 && status < 500;
    return isRefusal && typeof data?.message === 'string' && data.message ? data.message : t('sections.save-error');
};

interface SectionEditFormProps<T extends FieldValues> {
    methods: UseFormReturn<T>;
    /** Saves the section; a rejection keeps it open and shows the error in it. */
    onSave: (values: T) => Promise<void>;
    onCancel: () => void;
    /** Sends each save its validation refuses to analytics as this form. Without it, none is sent. */
    formKey?: FormKey;
    children: ReactNode;
}

// The form's own children, laid out by the read view's grid. A field is a cell and owns its span; a wrapper that
// is one field's cell says so with `form-field`, and one around several fields steps aside with `contents`. Any
// other block, such as an alert, a hint or a list editor, takes the whole row.
// `--field-hang` is how far a cell's control hangs out of it on the left, for its text to start where the read
// value's does. A block that is not a cell draws its own fields, which keep their boxes inside it.
const RECORD_FIELDS_CLASS = [
    'contents *:m-0! [&>*:not(.form-field)]:col-span-full',
    '[--field-hang:11px] [&>*:not(.form-field):not(.contents)]:[--field-hang:0px]',
].join(' ');

/**
 * A section's fields in edit, with Cancel and Save. Save validates the fields shown, which are the section's
 * only; Esc cancels and ⌘↵ (Ctrl+↵) saves; leaving with changes asks first.
 */
export function SectionEditForm<T extends FieldValues>({
    methods,
    onSave,
    onCancel,
    formKey,
    children,
}: SectionEditFormProps<T>) {
    const {
        handleSubmit,
        formState: { isDirty, isSubmitting },
    } = methods;
    const [saveError, setSaveError] = useState<string | null>(null);
    const fieldsRef = useRef<HTMLDivElement>(null);
    const [formEnd, setFormEnd] = useState<HTMLDivElement | null>(null);
    const readOnlyRows = useContext(SectionReadOnlyRowsContext);
    useSectionEditorReady();

    const save = handleSubmit(
        async (values) => {
            setSaveError(null);
            try {
                await onSave(values);
            } catch (error) {
                setSaveError(saveErrorText(error));
            }
        },
        formKey ? rejectedSubmitHandler(formKey) : undefined
    );

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        // A key pressed in an open dropdown, or in a picker's popup portalled out of the section, is the control's.
        if (
            event.defaultPrevented ||
            !fieldsRef.current?.contains(target) ||
            target.getAttribute('aria-expanded') === 'true'
        ) {
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
        } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            // One save at a time, as the Save button, off while one runs, has it: a second PUT of new rows inserts
            // them twice.
            if (!isSubmitting && !event.repeat) {
                void save();
            }
        }
    };

    return (
        <FormProvider {...methods}>
            <div ref={fieldsRef} onKeyDown={onKeyDown} className="flex flex-col gap-3.5">
                <div data-section-fields className={FIELD_GRID_CLASS}>
                    <div className={RECORD_FIELDS_CLASS}>
                        <InSectionEditFormContext.Provider value={true}>
                            <InRecordSectionContext.Provider value={true}>
                                <SectionFormEndContext.Provider value={formEnd}>
                                    {children}
                                </SectionFormEndContext.Provider>
                            </InRecordSectionContext.Provider>
                        </InSectionEditFormContext.Provider>
                    </div>
                    {readOnlyRows.length > 0 ? (
                        <dl className="contents">
                            <ReadOnlyRows rows={readOnlyRows} />
                        </dl>
                    ) : null}
                    {/* What a `SectionFormEnd` holds, laid out as the fields are. */}
                    <div ref={setFormEnd} className={RECORD_FIELDS_CLASS} />
                </div>
                <div className="border-neutral-tint bg-surface-tile -mx-[19px] flex min-h-[60px] flex-wrap items-center gap-x-2.5 gap-y-2 border-t px-[19px] py-3">
                    <span className="text-foreground-tertiary text-caption flex-auto">
                        <FormattedMessage id="sections.shortcuts" values={{ save: MOD_ENTER_HINT }} />
                    </span>
                    {saveError ? (
                        <span
                            role="alert"
                            className="text-error new:text-danger-ink text-caption flex items-start gap-1.5"
                        >
                            <CircleAlertIcon
                                aria-hidden
                                className="new:block mt-[calc((1lh-1rem)/2)] hidden size-4 shrink-0"
                            />
                            <span className="min-w-0">{saveError}</span>
                        </span>
                    ) : null}
                    <div className="ms-auto flex gap-2.5">
                        <Button
                            variant="outline"
                            className="border-border-control bg-card text-foreground h-9 px-3.5 shadow-none"
                            onClick={onCancel}
                        >
                            <FormattedMessage id="cancel" />
                        </Button>
                        {/* Off while a save runs, yet still focusable: a button disabled outright under the focus
                            that pressed it drops that focus to the page. The kit button takes no click meanwhile. */}
                        <Button
                            variant="secondary"
                            className={cn('h-9 px-3.5 font-semibold', OFF_SECONDARY_CLASS)}
                            disabled={isSubmitting}
                            focusableWhenDisabled
                            onClick={() => void save()}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                    </div>
                </div>
            </div>
            <SectionUnsavedChangesGuard when={isDirty} />
        </FormProvider>
    );
}
